import React, { useCallback, useEffect, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { CRUISE_UNIFIED_FILTERS } from 'constants/cruise'
import { resetSessionId } from 'actions/CruiseActions'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { useCruiseSearchEnabled, useGlobalCruisesSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalCruisesSearch'
import { useAppDispatch } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'

import { getCruiseSearchStateFromParams } from 'lib/cruises/cruiseUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'components/Luxkit/Typography/Heading'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import OfferList from 'components/OfferList/OfferList'
import {
  OfferListEventHandler,
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import Group from 'components/utils/Group'
import CruiseHeadData from '../Common/CruiseHeadData'
import CruisePageSearchHero from './CruisePageSearchHero'
import CruisePromoTiles from '../CruisePromoTiles/CruisePromoTiles'
import CruisePageDestinationsSection from './CruisePageDestinationsSection'
import CruisePageCruiseLinesSection from './CruisePageCruiseLinesSection/CruisePageCruiseLinesSection'
import CruisePageFlashOffersCarousel from './CruisePageFlashOffersCarousel'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOfferList from 'hooks/Offers/useOfferList'
import * as Analytics from 'analytics/analytics'
import Leaderboard from 'components/Common/Banner/Leaderboard'
import { connect } from 'react-redux'
import { fetchListBanners } from 'actions/ContentActions'
import { BANNER_LOCATION_CRUISES } from 'constants/bannerLocations'

const ListingsSection = styled(LayoutContainer)`
  padding-top: ${rem(36)};
  padding-bottom: ${rem(64)};
`

interface Props {
  banners?: Array<App.ListBanner>;
}

function CruisesPage(props: Props) {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  useEffect(() => {
    dispatch(fetchListBanners(BANNER_LOCATION_CRUISES))
  // eslint-disable-next-line
  }, [])

  const banners = props.banners || []

  const offerListTracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Cruise Page Offer List'),
    { ias: { list: 'cruises' } },
  )

  const heroTracking = useOfferListTracking(
    getListName('OfferList', 'CRUISE', 'Hero Carousel'),
    { ias: { list: 'cruises' } },
  )

  const isCruiseSearchEnabled = useCruiseSearchEnabled()
  const flashOfferList = useOfferList(CRUISE_UNIFIED_FILTERS)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialSearchState = useMemo(() => getCruiseSearchStateFromParams(queryParams), [])
  const { globalCruisesSearchDispatch, globalCruisesSearchState } = useGlobalCruisesSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    ...initialSearchState,
  })

  const onCruiseCarouselAnalyticEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      Analytics.trackClientEvent({
        subject: 'click-any-offer',
        action: 'clicked',
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '28377200726',
        optimizelyEventKey: 'click-any-offer',
      })
    }
  }, [])

  useEffect(() => {
    dispatch(resetSessionId())
  }, [dispatch, globalCruisesSearchDispatch])

  return (
    <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
      <div>
        <CruiseHeadData />

        <GlobalSearchContextProvider
          state={globalCruisesSearchState}
          dispatch={globalCruisesSearchDispatch}
        >
          <OfferListEventsProvider onListEvent={onCruiseCarouselAnalyticEvent}>
            <CruisePageSearchHero floatingSearchBar={!flashOfferList.fetching && !flashOfferList.offerIds.length} />
          </OfferListEventsProvider>
          <OfferListEventsProvider onListEvent={onCruiseCarouselAnalyticEvent} tracking={heroTracking} >
            <CruisePageFlashOffersCarousel offerList={flashOfferList} />
          </OfferListEventsProvider>

          <Pane>
            <ListingsSection>
              <Group direction="vertical" gap={48}>
                {isCruiseSearchEnabled && <>
                  {banners && <Leaderboard banners={banners} />}
                  <CruisePageDestinationsSection />
                  <CruisePromoTiles />
                  <CruisePageCruiseLinesSection />
                </>}

                <OfferListEventsProvider tracking={offerListTracking}>
                  <div>
                    <ScrollAnchor id="listings" />
                    <Group direction="vertical" gap={24}>
                      <Heading variant="heading2">Explore thousands of <i>incredible</i> cruise offers</Heading>
                      <OfferList filters={CRUISE_UNIFIED_FILTERS} excludedIds={flashOfferList.offerIds.slice(0, 2)} />
                    </Group>
                  </div>
                  <ErrorBoundary fallback={null}>
                    <PromptCardSelector tripReengagement />
                  </ErrorBoundary>
                </OfferListEventsProvider>
              </Group>

            </ListingsSection>
          </Pane>
        </GlobalSearchContextProvider>
      </div>
    </AnalyticsPageContext.Provider>
  )
}

function mapStateToProps(state: App.State) {
  return {
    banners: state.content.listBanners.cruises,
  }
}

export default connect(mapStateToProps)(CruisesPage)
